/**
 * Module Rulebooks — Registry + helpers.
 *
 * Strak-Trek Refactor — Phase 3.
 *
 * Single entry point for all per-module rulebooks. The policy engine
 * consults this registry first; if a module has no rulebook entry the
 * engine falls back to DEFAULT_POLICY (back-compat path).
 *
 * EXTENSION HOOKS — when adding new content surface area:
 *
 *  • New module     → create `<short>.rulebook.ts`, import it here, add it
 *                     to RULEBOOKS_REGISTRY, and update ModuleId in
 *                     src/content/types.ts.
 *  • New visual     → list it in the relevant rulebook's
 *                     `visuals.preferredAidTypes` and (if family-specific)
 *                     `visuals.familyVisualHints`.
 *  • New q-type     → list it in the relevant rulebook's
 *                     `questionTypes.preferredTypes`. For experimental /
 *                     custom types add a `customTypeRoles` entry with
 *                     `didacticRole` + `preferredDok` so the pipeline can
 *                     reason about DOK alignment without a global registry
 *                     change. This is how we scale to 100+ types.
 */

import type { ModuleId } from '../../content'
import type { ModuleRulebook } from './types'

import { GET_RULEBOOK } from './get.rulebook'
import { REK_RULEBOOK } from './rek.rulebook'
import { MET_RULEBOOK } from './met.rulebook'
import { TIJD_RULEBOOK } from './tijd.rulebook'
import { GELD_RULEBOOK } from './geld.rulebook'
import { SWW_RULEBOOK } from './sww.rulebook'
import { SPR_RULEBOOK } from './spr.rulebook'
import { TAA_RULEBOOK } from './taa.rulebook'
import { STU_RULEBOOK } from './stu.rulebook'
import { TOPO_RULEBOOK } from './topo.rulebook'

import { getQuestionTypesByVisualRole } from '../../content/questions/questionTypeRegistry'

export type { ModuleRulebook } from './types'
export type {
  TierBand,
  ModuleDidactics,
  ModuleNotationRules,
  ModuleContentRules,
  ModuleExplanationRules,
  ModuleVisualRules,
  ModuleQuestionTypeRules,
  ModuleQuestionTypeEntry,
  ModuleDistractorTrap,
  QuestionTypeDidacticRole,
  FamilyTypeHint,
} from './types'

// ─── Roadmap (aspirational future types + visuals) ───────────────────────────
export { getModuleRoadmap, countRoadmapEntries } from './roadmaps'
export type {
  ModuleRoadmap,
  FutureQuestionTypeEntry,
  FutureVisualEntry,
} from './roadmaps'

// ─── Registry ────────────────────────────────────────────────────────────────

/**
 * SSOT for per-module didactic rulebooks. Keys are canonical ModuleId
 * strings from src/content/types.ts.
 */
export const RULEBOOKS_REGISTRY: Record<ModuleId, ModuleRulebook> = {
  Getalbegrip: GET_RULEBOOK,
  Basisrekenen: REK_RULEBOOK,
  Metriek: MET_RULEBOOK,
  Tijd: TIJD_RULEBOOK,
  Geld: GELD_RULEBOOK,
  SpellingWW: SWW_RULEBOOK,
  SpellingRegel: SPR_RULEBOOK,
  Taalbegrip: TAA_RULEBOOK,
  Studievaardig: STU_RULEBOOK,
  Topo: TOPO_RULEBOOK,
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Get the rulebook for a module, or null when the module has no entry.
 */
export function getModuleRulebook(
  moduleId: ModuleId | string | undefined,
): ModuleRulebook | null {
  if (!moduleId) return null
  return RULEBOOKS_REGISTRY[moduleId as ModuleId] ?? null
}

/**
 * List all module rulebooks in display order (alphabetical by display name).
 */
export function listModuleRulebooks(): ModuleRulebook[] {
  return Object.values(RULEBOOKS_REGISTRY).sort((a, b) =>
    a.displayName.localeCompare(b.displayName, 'nl'),
  )
}

/**
 * Look up a rulebook by its short code (GET, REK, MET, …).
 * Useful for the admin UI where shortCode is the display key.
 */
export function getRulebookByShortCode(
  shortCode: string,
): ModuleRulebook | null {
  const code = shortCode.toUpperCase()
  for (const rb of Object.values(RULEBOOKS_REGISTRY)) {
    if (rb.shortCode === code) return rb
  }
  return null
}

/**
 * Total count of registered module rulebooks. Surfaced via systemStats.
 */
export function countModuleRulebooks(): number {
  return Object.keys(RULEBOOKS_REGISTRY).length
}

// ─── Family-level hint resolution ────────────────────────────────────────────

/**
 * Resolve all `familyTypeHints` whose `familyIdPattern` matches the given
 * familyId, scanning all rulebooks. Returns hints in registry-order; the
 * first match is typically the most specific.
 *
 * Used by:
 *   - The pedagogy stage to surface soft warnings when a seed's question
 *     type ∈ a matching hint's `discouragedTypes`.
 *   - Admin UI's "what fits this family" panel.
 *   - AI-seed-generation guidance.
 *
 * Pattern strings are compiled with `new RegExp(pattern)`. Invalid patterns
 * are silently skipped so a typo in one rulebook can't break the others.
 */
export function resolveFamilyTypeHints(
  familyId: string,
): Array<import('./types').FamilyTypeHint & { moduleId: ModuleId }> {
  if (!familyId) return []
  const matches: Array<
    import('./types').FamilyTypeHint & { moduleId: ModuleId }
  > = []
  for (const [moduleId, rb] of Object.entries(RULEBOOKS_REGISTRY) as Array<
    [ModuleId, ModuleRulebook]
  >) {
    const hints = rb.questionTypes.familyTypeHints
    if (!hints) continue
    for (const hint of hints) {
      try {
        if (new RegExp(hint.familyIdPattern).test(familyId)) {
          matches.push({ ...hint, moduleId })
        }
      } catch {
        // skip invalid regex in a single rulebook entry
      }
    }
  }
  return matches
}

// NOTE: stab-ronde 3 (mei 2026) — `isQuestionTypeDiscouragedForFamily`
// verwijderd: nul callers in `src/**`. Pedagogy-stage scant `discouragedTypes`
// nu direct via `resolveFamilyTypeHints(familyId)` zonder deze tussenhelper.

/**
 * Symmetric helper to `resolveFamilyTypeHints`: scans all rulebooks for
 * `familyVisualHints` whose `familyIdPattern` matches the given familyId.
 *
 * Used by the admin "Family-hints" panel to render — alongside the
 * preferred / discouraged question types — the preferred stimulus and
 * support visuals for the selected family. Returns matches in
 * registry-order; the first match is typically the most specific.
 */
export function resolveFamilyVisualHints(familyId: string): Array<
  NonNullable<ModuleRulebook['visuals']['familyVisualHints']>[number] & {
    moduleId: ModuleId
  }
> {
  if (!familyId) return []
  const matches: Array<
    NonNullable<ModuleRulebook['visuals']['familyVisualHints']>[number] & {
      moduleId: ModuleId
    }
  > = []
  for (const [moduleId, rb] of Object.entries(RULEBOOKS_REGISTRY) as Array<
    [ModuleId, ModuleRulebook]
  >) {
    const hints = rb.visuals.familyVisualHints
    if (!hints) continue
    for (const hint of hints) {
      try {
        if (new RegExp(hint.familyIdPattern).test(familyId)) {
          matches.push({ ...hint, moduleId })
        }
      } catch {
        // skip invalid regex in a single rulebook entry
      }
    }
  }
  return matches
}

/**
 * Returns the EFFECTIVE `questionTypeExempt` list for a family-visual-hint.
 *
 * Governance Structural Refactor 2026 — Fase 1 (SSOT consolidation).
 *
 * Combines:
 *   1. The hint's explicit `questionTypeExempt` (if any).
 *   2. When `autoExemptEmbedded` is true: every question type whose
 *      `visualRole === 'embedded'` in QUESTION_TYPE_VISUAL_ROLE.
 *
 * This lets a broad catch-all hint (e.g. `^GET-PROC-`) skip every
 * interactive type whose own renderer is the visual surface — without
 * enumerating them by hand and without drifting whenever a new embedded
 * type is added. Adding a new embedded question type now flows
 * automatically to every rulebook that uses `autoExemptEmbedded: true`.
 *
 * Returns a Set for O(1) `.has(type)` lookups by callers.
 */
export function deriveQuestionTypeExempt(
  hint: NonNullable<ModuleRulebook['visuals']['familyVisualHints']>[number],
): Set<string> {
  const exempt = new Set<string>(
    Array.isArray(hint.questionTypeExempt) ? hint.questionTypeExempt : [],
  )
  if (hint.autoExemptEmbedded) {
    for (const t of getQuestionTypesByVisualRole('embedded')) {
      exempt.add(t)
    }
  }
  return exempt
}
